import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { getAwsRum } from 'rum/initRum';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage } from 'react-intl';

// project imports
import { isChunkLoadError } from 'utils/chunkErrorUtils';
import tempNoInternetImage from 'assets/images/temp-no-internet.png';

// ==============================|| ELEMENT ERROR - COMMON ||============================== //

export default function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    const rum = getAwsRum();
    if (!rum || error == null) return;
    try {
      rum.recordError(error instanceof Error ? error : new Error(String(error)));
    } catch (_) {
      /* ignore */
    }
  }, [error]);

  const isBrowserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const errorText = `${error?.name || ''} ${error?.message || ''}`.toUpperCase();
  const isConnectivityError = errorText.includes('ERR_CONNECTION_TIMED_OUT') || errorText.includes('ERR_ADDRESS_UNREACHABLE');
  const isChunkError = isChunkLoadError(error);
  const shouldShowOfflineState = isChunkError || isBrowserOffline || isConnectivityError;

  // Handle chunk/network loading failures with connectivity-focused fallback
  if (shouldShowOfflineState) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
          gap: 2,
          p: 3
        }}
      >
        <Box
          component="img"
          src={tempNoInternetImage}
          alt="No internet connection"
          sx={{
            width: { xs: 160, sm: 220 },
            maxWidth: '100%',
            objectFit: 'contain'
          }}
        />
        <Typography variant="h4" color="text.primary" textAlign="center">
          <FormattedMessage id="networkConnectivityErrorTitle" />
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => window.location.reload()} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <Alert color="error">Error 404 - This page doesn&apos;t exist!</Alert>;
    }

    if (error.status === 401) {
      return <Alert color="error">Error 401 - You aren&apos;t authorized to see this</Alert>;
    }

    if (error.status === 503) {
      return <Alert color="error">Error 503 - Looks like our API is down</Alert>;
    }

    if (error.status === 418) {
      return <Alert color="error">Error 418 - Contact administrator</Alert>;
    }
  }

  return <Alert color="error">Under Maintenance</Alert>;
}
